
import {
  Component, Watch, Mixins, Prop,
} from 'vue-property-decorator';
import { AppTheme } from '@/features/common/constants';
import NavItem from '@/features/common/components/navbar/NavItem.vue';
import NavSubItem from '@/features/common/components/navbar/NavSubItem.vue';
import NavExpandItem from '@/features/common/components/navbar/NavExpandItem.vue';
import NavLogo from '@/features/common/components/navbar/NavLogo.vue';
import IconDashboard from '@/features/common/components/navbar/icons/IconDashboard.vue';
import IconCreateShipment from '@/features/common/components/navbar/icons/IconCreateShipment.vue';
import IconEstimate from '@/features/common/components/navbar/icons/IconEstimate.vue';
import IconHistory from '@/features/common/components/navbar/icons/IconHistory.vue';
import IconPickups from '@/features/common/components/navbar/icons/IconPickups.vue';
import IconConnect from '@/features/common/components/navbar/icons/IconConnect.vue';
import IconAccount from '@/features/common/components/navbar/icons/IconAccount.vue';
import { visitCreateShipmentPage } from '@/features/shipment/helpers/createShipment';
import WhiteLabelMixin from '../../mixins/whiteLabelMixin';

@Component({
  components: {
    NavItem,
    NavSubItem,
    NavExpandItem,
    NavLogo,
    IconDashboard,
    IconCreateShipment,
    IconEstimate,
    IconHistory,
    IconPickups,
    IconConnect,
    IconAccount,
  },
})
export default class Navbar extends Mixins(WhiteLabelMixin) {
  @Prop({ default: false }) topExpanded!: boolean;

  private icons = {
    dashboard: IconDashboard,
    createShipment: IconCreateShipment,
    estimate: IconEstimate,
    history: IconHistory,
    pickups: IconPickups,
    connect: IconConnect,
    account: IconAccount,
  };

  private drawer = true;

  private collapsed = false;

  private manualCollapsed = this.collapsed;

  private temporary = false;

  private get mini() {
    return this.temporary ? false : this.collapsed;
  }

  get isDashboardPage(): boolean {
    return this.$route.name === 'dashboard';
  }

  get navLogoStyle(): {[key: string]: string} {
    const padding = this.appTheme === AppTheme.Default ? '15px 20px 15px 20px' : '15px 20px';

    return {
      padding,
    };
  }

  get drawerScreenSize() {
    return this.isDashboardPage ? 1400 : 1200;
  }

  get miniRestriction() {
    return this.isDashboardPage ? { min: 1200, max: 1570 } : { min: 1200, max: 1470 };
  }

  @Watch('$route.name')
  private recalculateDrawer() {
    this.temporary = window.innerWidth < this.drawerScreenSize;
    this.resize();
  }

  created(): void {
    window.addEventListener('resize', this.resize);
  }

  beforeDestroy(): void {
    window.removeEventListener('resize', this.resize);
  }

  private resize() {
    this.drawer = window.innerWidth >= this.drawerScreenSize;

    if (this.isInMiniRestriction()) {
      const value = window.innerWidth >= this.miniRestriction.min
      && window.innerWidth <= this.miniRestriction.max;
      this.collapsed = value;
      this.temporary = !value;
      return;
    }

    this.temporary = window.innerWidth < this.drawerScreenSize;

    if (this.manualCollapsed) return;

    this.collapsed = window.innerWidth >= this.miniRestriction.min
      && window.innerWidth <= this.miniRestriction.max;
  }

  private handleCreateShipment() {
    this.closeIfSamePage('shipment-create');
    visitCreateShipmentPage();
  }

  private openNav() {
    this.handleResizeEvent(() => {
      this.drawer = true;
    });
  }

  private closeNavigationDrawer() {
    if (this.isInMiniRestriction()) {
      this.drawer = true;
      this.temporary = false;
      this.resize();
    }
  }

  private handleCollapseToggle() {
    this.handleResizeEvent(() => {
      this.temporary = !!this.collapsed;
      this.collapsed = !this.collapsed;
      this.manualCollapsed = !this.manualCollapsed;
    });
  }

  private handleNavToggle() {
    if (this.isInMiniRestriction()) {
      this.handleCollapseToggle();
      return;
    }

    if (this.temporary) {
      this.drawer = false;
      return;
    }

    this.handleResizeEvent(() => {
      this.collapsed = !this.collapsed;
      this.manualCollapsed = !this.manualCollapsed;
    });
  }

  private handleResizeEvent(callback: () => void) {
    window.removeEventListener('resize', this.resize);

    callback();

    setTimeout(() => {
      window.addEventListener('resize', this.resize);
    }, 1000);
  }

  private closeIfSamePage(routeName: string) {
    if (routeName === this.$route.name && this.temporary) {
      this.drawer = false;
    }
  }

  private isInMiniRestriction(): boolean {
    const windowWidth = window.innerWidth;
    return windowWidth >= this.drawerScreenSize && windowWidth <= this.miniRestriction.max;
  }
}
